import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { useToast } from '../hooks/use-toast';
import { 
  Shield, 
  Users, 
  Settings, 
  Mail, 
  Layout, 
  LogOut, 
  Plus, 
  Trash2, 
  Edit2, 
  Save, 
  X,
  Loader2,
  Lock,
  Eye,
  EyeOff,
  ArrowLeft,
  Send,
  Pause,
  Play,
  Download,
  Upload,
  Database,
  Clock,
  Calendar,
  RefreshCw,
  FileJson,
  HardDrive,
  MessageSquare,
  Bell,
  Copy,
  Check,
  UserCog,
  KeyRound,
  ChevronDown,
  ChevronUp,
  Activity,
  LogIn,
  FileEdit,
  PlusCircle,
  MinusCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Footer from './Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return sessionStorage.getItem('admin_auth') === 'true';
  });
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  
  // Admin data state
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [settings, setSettings] = useState({
    header: { title: 'Portfolio Tracker', showLogo: true },
    footer: { text: '', showVersion: true },
    smtp: { host: '', port: 587, username: '', password: '', fromEmail: '', enabled: false }
  });
  const [isLoading, setIsLoading] = useState(false);
  
  // Test email state
  const [testEmail, setTestEmail] = useState('');
  const [isSendingTest, setIsSendingTest] = useState(false);
  
  // User editing state
  const [editingUser, setEditingUser] = useState(null);
  const [newUser, setNewUser] = useState({ email: '', pin: '', role: 'user' });
  const [showAddUser, setShowAddUser] = useState(false);
  
  // Delete confirmation state (for users with stocks)
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    show: false,
    user: null,
    confirmEmail: '',
    isDeleting: false
  });
  
  // Password reset modal state
  const [passwordReset, setPasswordReset] = useState({
    show: false,
    user: null,
    newPassword: '',
    isResetting: false
  });
  
  // Emulation state
  const [isEmulating, setIsEmulating] = useState(false);
  
  // Activity log state
  const [activityLogs, setActivityLogs] = useState([]);
  const [activityLogTotal, setActivityLogTotal] = useState(0);
  const [activityLogOpen, setActivityLogOpen] = useState(false);
  const [activityLogLoading, setActivityLogLoading] = useState(false);
  const [isClearingLog, setIsClearingLog] = useState(false);
  
  // Role options
  const roleOptions = [
    { value: 'admin', label: 'Admin', color: 'bg-purple-100 text-purple-700' },
    { value: 'user', label: 'User', color: 'bg-blue-100 text-blue-700' },
    { value: 'viewer', label: 'Viewer', color: 'bg-slate-100 text-slate-700' }
  ];
  
  // Backup state
  const [backups, setBackups] = useState([]);
  const [backupSchedule, setBackupSchedule] = useState({
    enabled: false,
    frequency: 'daily',
    time: '02:00',
    day_of_week: 'sunday',
    retention_days: 30,
    max_backups: 10,
    backup_email: ''
  });
  const [isCreatingBackup, setIsCreatingBackup] = useState(false);
  const [isSendingTestEmail, setIsSendingTestEmail] = useState(false);
  // Backup panel collapse states
  const [scheduleOpen, setScheduleOpen] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [selectedBackupFile, setSelectedBackupFile] = useState(null);
  
  // Communications state
  const [communications, setCommunications] = useState([]);
  const [showAddCommunication, setShowAddCommunication] = useState(false);
  const [editingCommunication, setEditingCommunication] = useState(null);
  const [newCommunication, setNewCommunication] = useState({
    name: '',
    subject: '',
    template: '',
    type: 'portfolio_summary',
    enabled: true,
    schedule: { frequency: 'daily', time: '08:00', days: ['mon', 'tue', 'wed', 'thu', 'fri'] },
    recipients: 'email_enabled',
    specific_emails: []
  });
  const [isSendingCommunication, setIsSendingCommunication] = useState(null);
  
  // Email Activity Log state
  const [emailActivityLogs, setEmailActivityLogs] = useState([]);
  const [emailActivityTotal, setEmailActivityTotal] = useState(0);
  const [emailActivityFilter, setEmailActivityFilter] = useState('all');
  const [isLoadingActivity, setIsLoadingActivity] = useState(false);
  
  // Daily Summary state
  const [dailySummarySettings, setDailySummarySettings] = useState({
    enabled: false,
    time: '08:00',
    lastRun: null,
    lastSentCount: 0
  });
  const [isSendingDailySummary, setIsSendingDailySummary] = useState(false);
  const [dailySummaryPreview, setDailySummaryPreview] = useState(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  
  // Market Movers Stock List state
  const [marketMoversList, setMarketMoversList] = useState({
    source: 'default',
    symbols: [],
    count: 0,
    lastUpdated: null
  });
  const [marketMoversText, setMarketMoversText] = useState('');
  const [isLoadingMarketMovers, setIsLoadingMarketMovers] = useState(false);
  const [isSavingMarketMovers, setIsSavingMarketMovers] = useState(false);
  
  // Fetch data on auth
  useEffect(() => {
    if (isAuthenticated) {
      fetchUsers();
      fetchSettings();
      fetchBackups();
      fetchBackupSchedule();
      fetchCommunications();
      fetchDailySummarySettings();
      fetchMarketMoversList();
      fetchEmailActivity();
    }
  }, [isAuthenticated]);
  
  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);
    setLoginError('');
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setIsAuthenticated(true);
        sessionStorage.setItem('admin_auth', 'true');
        sessionStorage.setItem('admin_email', data.email);
        toast({ title: "Welcome Admin", description: "Successfully logged in" });
      } else {
        setLoginError(data.message || 'Invalid credentials');
      }
    } catch (error) {
      setLoginError('Connection error');
    } finally {
      setIsLoggingIn(false);
    }
  };
  
  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('admin_auth');
    sessionStorage.removeItem('admin_email');
    toast({ title: "Logged Out", description: "Admin session ended" });
    navigate('/');  // Redirect to main login page
  };
  
  const fetchUsers = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users`);
      const data = await response.json();
      setUsers(data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };
  
  const fetchSettings = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/settings`);
      const data = await response.json();
      if (data.settings) {
        setSettings(prev => ({ ...prev, ...data.settings }));
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };
  
  const fetchActivityLog = async () => {
    setActivityLogLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/activity-log?limit=200`);
      const data = await response.json();
      setActivityLogs(data.logs || []);
      setActivityLogTotal(data.total || 0);
    } catch (error) {
      console.error('Error fetching activity log:', error);
    } finally {
      setActivityLogLoading(false);
    }
  };
  
  const handleClearActivityLog = async () => {
    if (!window.confirm('Are you sure you want to clear the entire activity log?')) return;
    setIsClearingLog(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/activity-log`, { method: 'DELETE' });
      const data = await response.json();
      if (data.success) {
        setActivityLogs([]);
        setActivityLogTotal(0);
        toast({ title: "Success", description: `Cleared ${data.deleted} log entries` });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to clear activity log", variant: "destructive" });
    } finally {
      setIsClearingLog(false);
    }
  };
  
  const handleAddUser = async () => {
    if (!newUser.email || !newUser.pin) {
      toast({ title: "Error", description: "Email and PIN are required", variant: "destructive" });
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newUser)
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({ title: "Success", description: "User created successfully" });
        setNewUser({ email: '', pin: '', role: 'user' });
        setShowAddUser(false);
        fetchUsers();
      } else {
        toast({ title: "Error", description: data.detail || "Failed to create user", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to create user", variant: "destructive" });
    }
  };
  
  const handleUpdateUser = async (originalEmail) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users/${encodeURIComponent(originalEmail)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editingUser)
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({ title: "Success", description: "User updated successfully" });
        setEditingUser(null);
        fetchUsers();
      } else {
        toast({ title: "Error", description: "Failed to update user", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to update user", variant: "destructive" });
    }
  };
  
  const handleRoleChange = async (email, newRole) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users/${encodeURIComponent(email)}/role?role=${newRole}`, {
        method: 'PUT'
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({ title: "Role Updated", description: data.message });
        fetchUsers();
      } else {
        toast({ title: "Error", description: "Failed to update role", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to update role", variant: "destructive" });
    }
  };
  
  const handleToggleSuspend = async (email, currentSuspended) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users/${encodeURIComponent(email)}/suspend`, {
        method: 'PUT'
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({ 
          title: data.suspended ? "User Suspended" : "User Activated", 
          description: data.message 
        });
        fetchUsers();
      } else {
        toast({ title: "Error", description: "Failed to update user status", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to update user status", variant: "destructive" });
    }
  };

  const handleToggleEmailNotifications = async (email, currentStatus) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users/${encodeURIComponent(email)}/email-notifications`, {
        method: 'PUT'
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({ 
          title: data.emailNotifications ? "Email Enabled" : "Email Disabled", 
          description: `Email notifications ${data.emailNotifications ? 'enabled' : 'disabled'} for ${email}` 
        });
        fetchUsers();
      } else {
        toast({ title: "Error", description: "Failed to update email preference", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to update email preference", variant: "destructive" });
    }
  };
  
  const handleDeleteUser = async (email, forceDelete = false) => {
    // Find the user to check if they have stocks
    const user = users.find(u => u.email === email);
    
    // If user has stocks and we're not forcing delete, show confirmation dialog
    if (user && user.stockCount > 0 && !forceDelete) {
      setDeleteConfirmation({
        show: true,
        user: user,
        confirmEmail: '',
        isDeleting: false
      });
      return;
    }
    
    // Simple confirmation for users without stocks
    if (!forceDelete && !window.confirm(`Are you sure you want to delete ${email}?`)) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users/${encodeURIComponent(email)}`, {
        method: 'DELETE'
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({ title: "Success", description: "User deleted successfully" });
        fetchUsers();
        // Close confirmation dialog if open
        setDeleteConfirmation({ show: false, user: null, confirmEmail: '', isDeleting: false });
      } else {
        toast({ title: "Error", description: "Failed to delete user", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete user", variant: "destructive" });
    }
  };
  
  const handleConfirmDelete = async () => {
    if (!deleteConfirmation.user) return;
    
    // Verify email matches
    if (deleteConfirmation.confirmEmail !== deleteConfirmation.user.email) {
      toast({ 
        title: "Email doesn't match", 
        description: "Please type the exact email address to confirm deletion",
        variant: "destructive"
      });
      return;
    }
    
    setDeleteConfirmation(prev => ({ ...prev, isDeleting: true }));
    
    try {
      await handleDeleteUser(deleteConfirmation.user.email, true);
    } finally {
      setDeleteConfirmation({ show: false, user: null, confirmEmail: '', isDeleting: false });
    }
  };
  
  // Emulate user - login as the selected user
  const handleEmulateUser = async (userEmail) => {
    setIsEmulating(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users/${encodeURIComponent(userEmail)}/emulate`, {
        method: 'POST'
      });
      
      const data = await response.json();
      
      if (data.success && data.user) {
        // Store emulation info
        sessionStorage.setItem('emulated_user', JSON.stringify({
          email: data.user.email,
          role: data.user.role,
          emulated: true,
          adminEmail: sessionStorage.getItem('admin_email') || 'superadmin@admin.com'
        }));
        
        // Set the auth session data that PortfolioDashboard expects
        sessionStorage.setItem('portfolio_auth', 'true');
        sessionStorage.setItem('portfolio_user_email', data.user.email);
        sessionStorage.setItem('portfolio_user_role', data.user.role || 'user');
        
        // Store email in localStorage for remember feature
        localStorage.setItem('portfolio_email', data.user.email);
        
        toast({ 
          title: "Emulating User", 
          description: `Opening portfolio for ${data.user.email}` 
        });
        
        // Redirect to main dashboard
        setTimeout(() => {
          window.location.href = '/';
        }, 500);
      } else {
        toast({ title: "Error", description: "Failed to emulate user", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to emulate user", variant: "destructive" });
    } finally {
      setIsEmulating(false);
    }
  };
  
  // Reset user password
  const handleResetPassword = async () => {
    if (!passwordReset.user || !passwordReset.newPassword) {
      toast({ title: "Error", description: "Please enter a new password", variant: "destructive" });
      return;
    }
    
    if (passwordReset.newPassword.length < 4) {
      toast({ title: "Error", description: "Password must be at least 4 characters", variant: "destructive" });
      return;
    }
    
    setPasswordReset(prev => ({ ...prev, isResetting: true }));
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/users/${encodeURIComponent(passwordReset.user.email)}/reset-password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newPassword: passwordReset.newPassword })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({ title: "Success", description: `Password reset for ${passwordReset.user.email}` });
        setPasswordReset({ show: false, user: null, newPassword: '', isResetting: false });
      } else {
        toast({ title: "Error", description: data.message || "Failed to reset password", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to reset password", variant: "destructive" });
    } finally {
      setPasswordReset(prev => ({ ...prev, isResetting: false }));
    }
  };
  
  const handleSaveSettings = async (key) => {
    setIsLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/settings/${key}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key, value: settings[key] })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({ title: "Success", description: `${key.charAt(0).toUpperCase() + key.slice(1)} settings saved` });
      } else {
        toast({ title: "Error", description: "Failed to save settings", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to save settings", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleSendTestEmail = async () => {
    if (!testEmail) {
      toast({ title: "Error", description: "Please enter a test email address", variant: "destructive" });
      return;
    }
    
    setIsSendingTest(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/test-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ toEmail: testEmail })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({ title: "Success", description: data.message });
      } else {
        toast({ title: "Error", description: data.message, variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to send test email", variant: "destructive" });
    } finally {
      setIsSendingTest(false);
    }
  };
  
  // ==================== Backup Functions ====================
  
  const fetchBackups = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/backup/list`);
      const data = await response.json();
      setBackups(data.backups || []);
    } catch (error) {
      console.error('Error fetching backups:', error);
    }
  };
  
  const fetchBackupSchedule = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/backup/schedule`);
      const data = await response.json();
      if (data.schedule) {
        setBackupSchedule(data.schedule);
      }
    } catch (error) {
      console.error('Error fetching backup schedule:', error);
    }
  };
  
  const handleCreateBackup = async () => {
    setIsCreatingBackup(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/backup/create`);
      const data = await response.json();
      
      if (data.success) {
        // Download the backup
        const blob = new Blob([JSON.stringify(data.data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        toast({ title: "Backup Created", description: `Backup ${data.filename} downloaded successfully` });
        fetchBackups();
      } else {
        toast({ title: "Error", description: "Failed to create backup", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to create backup", variant: "destructive" });
    } finally {
      setIsCreatingBackup(false);
    }
  };
  
  const handleDownloadBackup = async (backupId, filename) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/backup/${backupId}`);
      const data = await response.json();
      
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast({ title: "Success", description: `Downloaded ${filename}` });
    } catch (error) {
      toast({ title: "Error", description: "Failed to download backup", variant: "destructive" });
    }
  };
  
  const handleDeleteBackup = async (backupId) => {
    if (!window.confirm('Are you sure you want to delete this backup?')) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/backup/${backupId}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      
      if (data.success) {
        toast({ title: "Success", description: "Backup deleted successfully" });
        fetchBackups();
      } else {
        toast({ title: "Error", description: "Failed to delete backup", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete backup", variant: "destructive" });
    }
  };
  
  const handleRestoreBackup = async (backupData) => {
    if (!window.confirm('WARNING: This will replace ALL current data with the backup. This cannot be undone. Are you sure?')) return;
    
    setIsRestoring(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/backup/restore`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ backup_data: backupData })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({ title: "Restore Complete", description: data.message });
        // Refresh all data
        fetchUsers();
        fetchSettings();
        fetchBackups();
      } else {
        toast({ title: "Error", description: "Failed to restore backup", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to restore backup", variant: "destructive" });
    } finally {
      setIsRestoring(false);
    }
  };
  
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.json')) {
      toast({ title: "Error", description: "Please select a valid JSON backup file", variant: "destructive" });
      return;
    }
    
    try {
      const text = await file.text();
      const backupData = JSON.parse(text);
      
      if (!backupData.collections) {
        toast({ title: "Error", description: "Invalid backup file format", variant: "destructive" });
        return;
      }
      
      setSelectedBackupFile({ name: file.name, data: backupData });
      toast({ title: "File Loaded", description: `Backup file "${file.name}" ready for restore` });
    } catch (error) {
      toast({ title: "Error", description: "Failed to read backup file", variant: "destructive" });
    }
    
    // Reset file input
    event.target.value = '';
  };
  
  const handleSaveSchedule = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/backup/schedule`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(backupSchedule)
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({ title: "Success", description: "Backup schedule saved successfully" });
      } else {
        toast({ title: "Error", description: "Failed to save schedule", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to save schedule", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleTestBackupEmail = async () => {
    if (!backupSchedule.backup_email) {
      toast({ title: "Error", description: "Please enter an email address first", variant: "destructive" });
      return;
    }
    setIsSendingTestEmail(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/backup/test-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: backupSchedule.backup_email })
      });
      const data = await response.json();
      if (data.success) {
        toast({ title: "Success", description: `Test backup email sent to ${backupSchedule.backup_email}` });
      } else {
        toast({ title: "Error", description: data.detail || "Failed to send test email", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to send test email", variant: "destructive" });
    } finally {
      setIsSendingTestEmail(false);
    }
  };
  
  // ==================== Communications Functions ====================
  
  const fetchCommunications = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/communications`);
      const data = await response.json();
      if (data.communications) {
        setCommunications(data.communications);
      }
    } catch (error) {
      console.error('Error fetching communications:', error);
    }
  };
  
  const fetchEmailActivity = async (filter = 'all') => {
    try {
      setIsLoadingActivity(true);
      const url = filter === 'all' 
        ? `${BACKEND_URL}/api/admin/email-activity?limit=100`
        : `${BACKEND_URL}/api/admin/email-activity?limit=100&type_filter=${filter}`;
      const response = await fetch(url);
      const data = await response.json();
      if (data.success) {
        setEmailActivityLogs(data.logs || []);
        setEmailActivityTotal(data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching email activity:', error);
    } finally {
      setIsLoadingActivity(false);
    }
  };
  
  const handleClearActivityLogs = async (olderThanDays = null) => {
    try {
      const url = olderThanDays 
        ? `${BACKEND_URL}/api/admin/email-activity/clear?older_than_days=${olderThanDays}`
        : `${BACKEND_URL}/api/admin/email-activity/clear`;
      const response = await fetch(url, { method: 'DELETE' });
      const data = await response.json();
      if (data.success) {
        toast({ title: "Success", description: data.message });
        fetchEmailActivity(emailActivityFilter);
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to clear logs", variant: "destructive" });
    }
  };
  
  const handleCreateCommunication = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/admin/communications`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newCommunication)
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({ title: "Success", description: "Communication created successfully" });
        fetchCommunications();
        setShowAddCommunication(false);
        setNewCommunication({
          name: '',
          subject: '',
          template: '',
          type: 'portfolio_summary',
          enabled: true,
          schedule: { frequency: 'daily', time: '08:00', days: ['mon', 'tue', 'wed', 'thu', 'fri'] },
          recipients: 'email_enabled',
          specific_emails: []
        });
      } else {
        toast({ title: "Error", description: "Failed to create communication", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to create communication", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleUpdateCommunication = async () => {
    if (!editingCommunication) return;
    
    try {
      setIsLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/admin/communications/${editingCommunication.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editingCommunication)
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({ title: "Success", description: "Communication updated successfully" });
        fetchCommunications();
        setEditingCommunication(null);
      } else {
        toast({ title: "Error", description: "Failed to update communication", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to update communication", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleDeleteCommunication = async (commId) => {
    if (!window.confirm('Are you sure you want to delete this communication?')) return;
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/communications/${commId}`, {
        method: 'DELETE'
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({ title: "Success", description: "Communication deleted" });
        fetchCommunications();
      } else {
        toast({ title: "Error", description: "Failed to delete communication", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete communication", variant: "destructive" });
    }
  };
  
  const handleToggleCommunication = async (commId) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/communications/${commId}/toggle`, {
        method: 'POST'
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({ title: data.enabled ? "Enabled" : "Disabled", description: data.message });
        fetchCommunications();
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to toggle communication", variant: "destructive" });
    }
  };
  
  const handleSendNow = async (commId) => {
    try {
      setIsSendingCommunication(commId);
      const response = await fetch(`${BACKEND_URL}/api/admin/communications/${commId}/send-now`, {
        method: 'POST'
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({ title: "Sent!", description: data.message });
        fetchCommunications();
      } else {
        toast({ title: "Error", description: data.message || "Failed to send", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to send communication", variant: "destructive" });
    } finally {
      setIsSendingCommunication(null);
    }
  };
  
  // Daily Summary Functions
  const fetchDailySummarySettings = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/daily-summary`);
      const data = await response.json();
      if (data.settings) {
        setDailySummarySettings(data.settings);
      }
    } catch (error) {
      console.error('Error fetching daily summary settings:', error);
    }
  };
  
  const handleSaveDailySummarySettings = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${BACKEND_URL}/api/admin/daily-summary`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dailySummarySettings)
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({ title: "Success", description: "Daily summary settings saved" });
        fetchDailySummarySettings();
      } else {
        toast({ title: "Error", description: data.message || "Failed to save settings", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to save settings", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleSendDailySummaryNow = async () => {
    try {
      setIsSendingDailySummary(true);
      const response = await fetch(`${BACKEND_URL}/api/admin/daily-summary/send-now`, {
        method: 'POST'
      });
      
      const data = await response.json();
      
      if (data.success) {
        const errDetail = data.errors && data.errors.length > 0 ? `\nErrors: ${data.errors.join(', ')}` : '';
        toast({ 
          title: data.sent_count > 0 ? "Sent!" : "Warning", 
          description: data.message + errDetail,
          variant: data.sent_count > 0 ? "default" : "destructive",
          duration: 10000
        });
        fetchDailySummarySettings();
      } else {
        toast({ title: "Error", description: data.message || "Failed to send", variant: "destructive", duration: 10000 });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to send daily summary", variant: "destructive" });
    } finally {
      setIsSendingDailySummary(false);
    }
  };
  
  const handlePreviewDailySummary = async () => {
    try {
      setIsLoadingPreview(true);
      const response = await fetch(`${BACKEND_URL}/api/admin/daily-summary/preview`, {
        method: 'POST'
      });
      
      const data = await response.json();
      
      if (data.success) {
        setDailySummaryPreview(data.html);
      } else {
        toast({ title: "Error", description: data.message || "Failed to generate preview", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to generate preview", variant: "destructive" });
    } finally {
      setIsLoadingPreview(false);
    }
  };
  
  // Market Movers Stock List functions
  const fetchMarketMoversList = async () => {
    try {
      setIsLoadingMarketMovers(true);
      const response = await fetch(`${BACKEND_URL}/api/admin/market-movers-list`);
      const data = await response.json();
      if (data.success) {
        setMarketMoversList(data);
        setMarketMoversText(data.symbols.join('\n'));
      }
    } catch (error) {
      console.error('Error fetching market movers list:', error);
    } finally {
      setIsLoadingMarketMovers(false);
    }
  };
  
  const handleSaveMarketMoversList = async () => {
    try {
      setIsSavingMarketMovers(true);
      // Parse the text into symbols (one per line, or comma/space separated)
      const symbols = marketMoversText
        .split(/[\n,\s]+/)
        .map(s => s.trim().toUpperCase())
        .filter(s => s.length > 0 && /^[A-Z.:]+$/.test(s));
      
      if (symbols.length === 0) {
        toast({ title: "Error", description: "Please enter at least one valid stock symbol", variant: "destructive" });
        return;
      }
      
      const response = await fetch(`${BACKEND_URL}/api/admin/market-movers-list`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ symbols })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({ title: "Success", description: data.message });
        fetchMarketMoversList();
      } else {
        toast({ title: "Error", description: data.error || "Failed to save", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to save market movers list", variant: "destructive" });
    } finally {
      setIsSavingMarketMovers(false);
    }
  };
  
  const handleResetMarketMoversList = async () => {
    if (!window.confirm('Are you sure you want to reset to the default stock list? This cannot be undone.')) {
      return;
    }
    
    try {
      setIsSavingMarketMovers(true);
      const response = await fetch(`${BACKEND_URL}/api/admin/market-movers-list/reset`, {
        method: 'POST'
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast({ title: "Reset", description: data.message });
        fetchMarketMoversList();
      } else {
        toast({ title: "Error", description: data.error || "Failed to reset", variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to reset market movers list", variant: "destructive" });
    } finally {
      setIsSavingMarketMovers(false);
    }
  };
  
  const handleDownloadMarketMoversList = () => {
    const blob = new Blob([marketMoversText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'market_movers_stocks.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast({ title: "Downloaded", description: "Stock list saved to file" });
  };
  
  const handleUploadMarketMoversList = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result;
      if (typeof content === 'string') {
        setMarketMoversText(content);
        toast({ title: "Loaded", description: `Loaded ${file.name}. Click Save to apply changes.` });
      }
    };
    reader.readAsText(file);
    e.target.value = ''; // Reset input
  };
  
  const communicationTypes = [
    { value: 'portfolio_summary', label: 'Portfolio Summary', icon: '📊' },
    { value: 'market_alert', label: 'Market Alert', icon: '📈' },
    { value: 'custom', label: 'Custom Message', icon: '✉️' }
  ];
  
  const frequencyOptions = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' }
  ];
  
  const dayOptions = [
    { value: 'mon', label: 'Mon' },
    { value: 'tue', label: 'Tue' },
    { value: 'wed', label: 'Wed' },
    { value: 'thu', label: 'Thu' },
    { value: 'fri', label: 'Fri' },
    { value: 'sat', label: 'Sat' },
    { value: 'sun', label: 'Sun' }
  ];
  
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };
  
  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col">
        <div className="flex-1 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-slate-700 shadow-2xl bg-slate-800/90 backdrop-blur">
          <CardHeader className="text-center pb-4 pt-8">
            <div className="flex justify-center mb-4">
              <img 
                src="/logo.png" 
                alt="PortfolioASaurus Admin" 
                className="w-28 h-28 object-contain drop-shadow-lg"
              />
            </div>
            <CardTitle className="text-3xl font-bold text-white mb-2">Admin Portal</CardTitle>
            <CardDescription className="text-slate-400">
              Restricted Access - Administrators Only
            </CardDescription>
          </CardHeader>
          <CardContent className="pb-8">
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-300">Admin Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@example.com"
                  className="h-12 bg-slate-900/50 border-slate-600 text-white"
                  disabled={isLoggingIn}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-300">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="h-12 bg-slate-900/50 border-slate-600 text-white pr-10"
                    disabled={isLoggingIn}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              
              {loginError && (
                <div className="text-red-400 text-sm text-center bg-red-500/10 py-2 rounded border border-red-500/20">
                  {loginError}
                </div>
              )}
              
              <Button 
                type="submit" 
                className="w-full bg-blue-600 hover:bg-blue-700 h-12"
                disabled={isLoggingIn || !email || !password}
              >
                {isLoggingIn ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying...</>
                ) : (
                  <><Lock className="w-4 h-4 mr-2" /> Access Admin Panel</>
                )}
              </Button>
            </form>
            
            <div className="mt-6 text-center">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/')}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to Portfolio
              </Button>
            </div>
          </CardContent>
        </Card>
        </div>
        <Footer />
      </div>
    );
  }
  
  // Admin Dashboard
  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      {/* Header */}
      <header className="bg-slate-800 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="PortfolioASaurus" className="w-12 h-12 object-contain" />
            <div>
              <h1 className="text-xl font-bold">Admin Dashboard</h1>
              <p className="text-xs text-slate-400">{sessionStorage.getItem('admin_email')}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline" 
              onClick={() => navigate('/')}
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Portfolio
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleLogout}
              className="bg-red-600 hover:bg-red-700"
            >
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>
      
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {[
            { id: 'users', label: 'Users', icon: Users },
            { id: 'backup', label: 'Backup', icon: Database },
            { id: 'market', label: 'Market Movers', icon: RefreshCw },
            { id: 'communications', label: 'Communications', icon: MessageSquare },
            { id: 'header', label: 'Header', icon: Layout },
            { id: 'footer', label: 'Footer', icon: Layout },
            { id: 'smtp', label: 'SMTP', icon: Mail }
          ].map(tab => (
            <Button
              key={tab.id}
              variant={activeTab === tab.id ? 'default' : 'outline'}
              onClick={() => setActiveTab(tab.id)}
              className={`justify-center ${activeTab === tab.id ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.label}
            </Button>
          ))}
        </div>
        
        {/* Users Panel */}
        {activeTab === 'users' && (
          <>
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5" /> User Management
                  </CardTitle>
                  <CardDescription>Manage portfolio users</CardDescription>
                </div>
                <Button onClick={() => setShowAddUser(true)} className="bg-emerald-600 hover:bg-emerald-700">
                  <Plus className="w-4 h-4 mr-2" /> Add User
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Add User Form */}
              {showAddUser && (
                <div className="mb-6 p-4 bg-slate-50 rounded-lg border">
                  <h3 className="font-semibold mb-3">Add New User</h3>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={newUser.email}
                        onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                        placeholder="user@example.com"
                      />
                    </div>
                    <div>
                      <Label>PIN / Password</Label>
                      <Input
                        type="text"
                        value={newUser.pin}
                        onChange={(e) => setNewUser({ ...newUser, pin: e.target.value })}
                        placeholder="Enter PIN or password"
                        maxLength={12}
                      />
                    </div>
                    <div>
                      <Label>Role</Label>
                      <select
                        value={newUser.role}
                        onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                        className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm"
                      >
                        {roleOptions.map(role => (
                          <option key={role.value} value={role.value}>{role.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-end gap-2">
                      <Button onClick={handleAddUser} className="bg-emerald-600 hover:bg-emerald-700">
                        <Save className="w-4 h-4 mr-2" /> Save
                      </Button>
                      <Button variant="outline" onClick={() => { setShowAddUser(false); setNewUser({ email: '', pin: '', role: 'user' }); }}>
                        <X className="w-4 h-4 mr-2" /> Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Users Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-slate-50">
                      <th className="text-left p-3 font-semibold">Email</th>
                      <th className="text-left p-3 font-semibold">Role</th>
                      <th className="text-right p-3 font-semibold">Portfolio Value</th>
                      <th className="text-left p-3 font-semibold">Status</th>
                      <th className="text-center p-3 font-semibold">Email</th>
                      <th className="text-left p-3 font-semibold">Created</th>
                      <th className="text-right p-3 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user, index) => {
                      const roleOption = roleOptions.find(r => r.value === user.role) || roleOptions[1];
                      return (
                        <tr key={user.email} className={`${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'} ${user.suspended ? 'opacity-60' : ''}`}>
                        <td className="p-3">
                          {editingUser?.originalEmail === user.email ? (
                            <Input
                              value={editingUser.email}
                              onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                              className="h-8"
                            />
                          ) : (
                            <button
                              onClick={() => handleEmulateUser(user.email)}
                              disabled={isEmulating || user.suspended}
                              className={`text-left hover:text-blue-600 hover:underline transition-colors ${user.suspended ? 'line-through text-slate-400 cursor-not-allowed' : 'text-slate-700'}`}
                              title={user.suspended ? 'Cannot emulate suspended user' : `Click to view portfolio as ${user.email}`}
                            >
                              {isEmulating ? (
                                <span className="flex items-center gap-1">
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                  {user.email}
                                </span>
                              ) : (
                                <span className="flex items-center gap-1">
                                  <UserCog className="w-3 h-3 opacity-50" />
                                  {user.email}
                                </span>
                              )}
                            </button>
                          )}
                        </td>
                        <td className="p-3">
                          <select
                            value={user.role || 'user'}
                            onChange={(e) => handleRoleChange(user.email, e.target.value)}
                            className={`px-2 py-1 rounded-full text-xs font-medium border-0 cursor-pointer ${roleOption.color}`}
                          >
                            {roleOptions.map(role => (
                              <option key={role.value} value={role.value}>{role.label}</option>
                            ))}
                          </select>
                        </td>
                        <td className="p-3 text-right">
                          <div className="flex flex-col items-end">
                            <span className="font-semibold text-slate-800">
                              ${(user.portfolioValue || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                            <span className="text-xs text-slate-500">
                              {user.stockCount > 0 ? `${user.stockCount} position${user.stockCount > 1 ? 's' : ''}` : 'No positions'}
                            </span>
                          </div>
                        </td>
                        <td className="p-3">
                          {user.suspended ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                              <Pause className="w-3 h-3 mr-1" /> Suspended
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                              <Play className="w-3 h-3 mr-1" /> Active
                            </span>
                          )}
                        </td>
                        <td className="p-3 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <Switch
                              checked={user.emailNotifications !== false}
                              onCheckedChange={() => handleToggleEmailNotifications(user.email, user.emailNotifications !== false)}
                              className="data-[state=checked]:bg-emerald-500"
                            />
                            <Mail className={`w-4 h-4 ${user.emailNotifications !== false ? 'text-emerald-500' : 'text-slate-300'}`} />
                          </div>
                        </td>
                        <td className="p-3 text-slate-500 text-sm">
                          {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="p-3 text-right">
                          {editingUser?.originalEmail === user.email ? (
                            <div className="flex justify-end gap-2">
                              <Button size="sm" onClick={() => handleUpdateUser(user.email)} className="bg-emerald-600 hover:bg-emerald-700">
                                <Save className="w-4 h-4" />
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setEditingUser(null)}>
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ) : (
                            <div className="flex justify-end gap-2">
                              <Button 
                                size="sm" 
                                variant={user.suspended ? "default" : "outline"}
                                onClick={() => handleToggleSuspend(user.email, user.suspended)}
                                className={user.suspended ? "bg-green-600 hover:bg-green-700" : "text-amber-600 border-amber-600 hover:bg-amber-50"}
                                title={user.suspended ? "Activate user" : "Suspend user"}
                              >
                                {user.suspended ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => setPasswordReset({ show: true, user: user, newPassword: '', isResetting: false })}
                                className="text-purple-600 border-purple-600 hover:bg-purple-50"
                                title="Reset password"
                              >
                                <KeyRound className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => setEditingUser({ originalEmail: user.email, email: user.email, pin: user.pin })}
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="destructive"
                                onClick={() => handleDeleteUser(user.email)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {users.length === 0 && (
                  <div className="text-center py-8 text-slate-500">No users found</div>
                )}
              </div>
            </CardContent>
          </Card>
          
          {/* Activity Log - Collapsible */}
          <Card className="mt-6">
            <CardHeader 
              className="cursor-pointer select-none hover:bg-slate-50 transition-colors"
              onClick={() => {
                const next = !activityLogOpen;
                setActivityLogOpen(next);
                if (next && activityLogs.length === 0) fetchActivityLog();
              }}
            >
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <Activity className="w-5 h-5 text-blue-600" />
                  <CardTitle className="text-base" data-testid="activity-log-title">User Activity Log</CardTitle>
                  {activityLogTotal > 0 && (
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                      {activityLogTotal}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {activityLogOpen && activityLogs.length > 0 && (
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={(e) => { e.stopPropagation(); handleClearActivityLog(); }}
                      disabled={isClearingLog}
                      className="text-red-600 border-red-200 hover:bg-red-50 text-xs h-7"
                      data-testid="clear-activity-log-btn"
                    >
                      {isClearingLog ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Trash2 className="w-3 h-3 mr-1" />}
                      Clear Log
                    </Button>
                  )}
                  {activityLogOpen && (
                    <Button 
                      size="sm" 
                      variant="ghost"
                      onClick={(e) => { e.stopPropagation(); fetchActivityLog(); }}
                      className="text-slate-500 h-7 w-7 p-0"
                      title="Refresh"
                    >
                      <RefreshCw className={`w-3.5 h-3.5 ${activityLogLoading ? 'animate-spin' : ''}`} />
                    </Button>
                  )}
                  {activityLogOpen ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                </div>
              </div>
            </CardHeader>
            {activityLogOpen && (
              <CardContent>
                {activityLogLoading && activityLogs.length === 0 ? (
                  <div className="flex items-center justify-center py-8 text-slate-400">
                    <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading activity log...
                  </div>
                ) : activityLogs.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">No activity recorded yet</div>
                ) : (
                  <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-white z-10">
                        <tr className="border-b bg-slate-50">
                          <th className="text-left p-2.5 font-semibold text-slate-600 w-[180px]">Timestamp</th>
                          <th className="text-left p-2.5 font-semibold text-slate-600">User</th>
                          <th className="text-left p-2.5 font-semibold text-slate-600 w-[140px]">Action</th>
                          <th className="text-left p-2.5 font-semibold text-slate-600">Details</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activityLogs.map((log, i) => {
                          const actionConfig = {
                            login: { icon: LogIn, color: 'text-emerald-600 bg-emerald-50', label: 'Login' },
                            profile_update: { icon: FileEdit, color: 'text-blue-600 bg-blue-50', label: 'Profile Update' },
                            stock_add: { icon: PlusCircle, color: 'text-green-600 bg-green-50', label: 'Stock Added' },
                            asset_add: { icon: PlusCircle, color: 'text-teal-600 bg-teal-50', label: 'Asset Added' },
                            asset_update: { icon: Edit2, color: 'text-amber-600 bg-amber-50', label: 'Asset Updated' },
                            asset_delete: { icon: MinusCircle, color: 'text-red-600 bg-red-50', label: 'Asset Deleted' },
                          };
                          const config = actionConfig[log.action] || { icon: Activity, color: 'text-slate-600 bg-slate-50', label: log.action };
                          const ActionIcon = config.icon;
                          return (
                            <tr key={i} className={`border-b border-slate-100 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'} hover:bg-blue-50/30`}>
                              <td className="p-2.5 text-slate-500 whitespace-nowrap font-mono text-xs">
                                {new Date(log.timestamp).toLocaleString()}
                              </td>
                              <td className="p-2.5 text-slate-700 font-medium">{log.user}</td>
                              <td className="p-2.5">
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
                                  <ActionIcon className="w-3 h-3" />
                                  {config.label}
                                </span>
                              </td>
                              <td className="p-2.5 text-slate-600">{log.details}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            )}
          </Card>
        </>
        )}
        
        {/* Backup & Restore Panel */}
        {activeTab === 'backup' && (
          <div className="space-y-4">
            {/* 1. Automatic Backup Schedule Card */}
            <Card>
              <CardHeader className="cursor-pointer select-none py-4" onClick={() => setScheduleOpen(!scheduleOpen)}>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Clock className="w-5 h-5" /> Automatic Backup Schedule
                    </CardTitle>
                    <CardDescription>Configure automatic scheduled backups</CardDescription>
                  </div>
                  {scheduleOpen ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                </div>
              </CardHeader>
              {scheduleOpen && (
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="scheduleEnabled"
                    checked={backupSchedule.enabled}
                    onChange={(e) => setBackupSchedule({ ...backupSchedule, enabled: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="scheduleEnabled" className="font-semibold">Enable Automatic Backups</Label>
                </div>
                
                {backupSchedule.enabled && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4 p-4 bg-slate-50 rounded-lg">
                    <div>
                      <Label>Frequency</Label>
                      <select
                        value={backupSchedule.frequency}
                        onChange={(e) => setBackupSchedule({ ...backupSchedule, frequency: e.target.value })}
                        className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm mt-1"
                      >
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                      </select>
                    </div>
                    <div>
                      <Label>Time (24h format)</Label>
                      <Input
                        type="time"
                        value={backupSchedule.time}
                        onChange={(e) => setBackupSchedule({ ...backupSchedule, time: e.target.value })}
                        className="mt-1"
                      />
                    </div>
                    {backupSchedule.frequency === 'weekly' && (
                      <div>
                        <Label>Day of Week</Label>
                        <select
                          value={backupSchedule.day_of_week}
                          onChange={(e) => setBackupSchedule({ ...backupSchedule, day_of_week: e.target.value })}
                          className="w-full h-10 px-3 rounded-md border border-slate-300 bg-white text-sm mt-1"
                        >
                          <option value="monday">Monday</option>
                          <option value="tuesday">Tuesday</option>
                          <option value="wednesday">Wednesday</option>
                          <option value="thursday">Thursday</option>
                          <option value="friday">Friday</option>
                          <option value="saturday">Saturday</option>
                          <option value="sunday">Sunday</option>
                        </select>
                      </div>
                    )}
                    <div>
                      <Label>Retention (days)</Label>
                      <Input
                        type="number"
                        value={backupSchedule.retention_days}
                        onChange={(e) => setBackupSchedule({ ...backupSchedule, retention_days: parseInt(e.target.value) || 30 })}
                        min="1"
                        max="365"
                        className="mt-1"
                      />
                    </div>
                  </div>
                )}
                
                <div className="border-t border-slate-200 pt-4 mt-4">
                  <Label className="font-semibold">Email Backups To</Label>
                  <p className="text-xs text-slate-400 mb-2">Automated backups will be emailed as a JSON attachment</p>
                  <div className="flex items-center gap-2">
                    <Input
                      type="email"
                      value={backupSchedule.backup_email || ''}
                      onChange={(e) => setBackupSchedule({ ...backupSchedule, backup_email: e.target.value })}
                      placeholder="backup@example.com"
                      className="flex-1"
                      data-testid="backup-email-input"
                    />
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handleTestBackupEmail} 
                      disabled={isSendingTestEmail || !backupSchedule.backup_email}
                      data-testid="test-backup-email-btn"
                    >
                      {isSendingTestEmail ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />}
                      {isSendingTestEmail ? 'Sending...' : 'Test'}
                    </Button>
                  </div>
                </div>
                
                {(() => {
                  const lastScheduled = backups
                    .filter(b => b.backup_type === 'scheduled')
                    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
                  return lastScheduled ? (
                    <p className="text-sm text-slate-500">
                      Last automated backup: <span className="font-medium text-slate-700">{new Date(lastScheduled.created_at).toLocaleString()}</span>
                    </p>
                  ) : (
                    <p className="text-sm text-slate-400">No automated backups have run yet.</p>
                  );
                })()}
                
                <Button onClick={handleSaveSchedule} disabled={isLoading}>
                  {isLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Schedule
                </Button>
              </CardContent>
              )}
            </Card>

            {/* 2. Create Backup Card */}
            <Card>
              <CardHeader className="cursor-pointer select-none py-4" onClick={() => setCreateOpen(!createOpen)}>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Download className="w-5 h-5" /> Create Backup
                    </CardTitle>
                    <CardDescription>Download a full backup of all application data</CardDescription>
                  </div>
                  {createOpen ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                </div>
              </CardHeader>
              {createOpen && (
              <CardContent>
                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                  <Button 
                    onClick={handleCreateBackup} 
                    disabled={isCreatingBackup}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    {isCreatingBackup ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating Backup...</>
                    ) : (
                      <><Download className="w-4 h-4 mr-2" /> Download Backup</>
                    )}
                  </Button>
                  <p className="text-sm text-slate-500">
                    Includes: Users, Admins, Stocks, Settings, Portfolio History
                  </p>
                </div>
              </CardContent>
              )}
            </Card>
            
            {/* 3. Restore Backup Card */}
            <Card>
              <CardHeader className="cursor-pointer select-none py-4" onClick={() => setRestoreOpen(!restoreOpen)}>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Upload className="w-5 h-5" /> Restore Backup
                    </CardTitle>
                    <CardDescription>Upload a backup file to restore all data (full replace)</CardDescription>
                  </div>
                  {restoreOpen ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                </div>
              </CardHeader>
              {restoreOpen && (
              <CardContent className="space-y-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <Label htmlFor="backupFile" className="cursor-pointer">
                      <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                        <FileJson className="w-10 h-10 mx-auto text-slate-400 mb-2" />
                        <p className="text-sm text-slate-600">Click to select backup file (.json)</p>
                        {selectedBackupFile && (
                          <p className="mt-2 text-sm font-medium text-blue-600">{selectedBackupFile.name}</p>
                        )}
                      </div>
                    </Label>
                    <input
                      id="backupFile"
                      type="file"
                      accept=".json"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                  </div>
                </div>
                
                {selectedBackupFile && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-1">
                        <h4 className="font-semibold text-amber-800">Ready to Restore</h4>
                        <p className="text-sm text-amber-700 mt-1">
                          File: {selectedBackupFile.name}
                        </p>
                        {selectedBackupFile.data?.metadata && (
                          <p className="text-sm text-amber-600 mt-1">
                            Backup Date: {new Date(selectedBackupFile.data.metadata.created_at).toLocaleString()}
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => handleRestoreBackup(selectedBackupFile.data)}
                          disabled={isRestoring}
                          className="bg-amber-600 hover:bg-amber-700"
                        >
                          {isRestoring ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Restoring...</>
                          ) : (
                            <><RefreshCw className="w-4 h-4 mr-2" /> Restore Now</>
                          )}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => setSelectedBackupFile(null)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <p className="text-xs text-amber-600 mt-3">
                      Warning: Restoring will replace ALL current data. This cannot be undone.
                    </p>
                  </div>
                )}
              </CardContent>
              )}
            </Card>
            
            {/* 4. Backup History Card */}
            <Card>
              <CardHeader className="cursor-pointer select-none py-4" onClick={() => setHistoryOpen(!historyOpen)}>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <HardDrive className="w-5 h-5" /> Backup History
                    </CardTitle>
                    <CardDescription>Previously created backups stored on server</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); fetchBackups(); }}>
                      <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                    </Button>
                    {historyOpen ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
                  </div>
                </div>
              </CardHeader>
              {historyOpen && (
              <CardContent>
                {backups.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Database className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p>No backups found</p>
                    <p className="text-sm">Create your first backup using the button above</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b bg-slate-50">
                          <th className="text-left p-3 font-semibold">Filename</th>
                          <th className="text-left p-3 font-semibold">Type</th>
                          <th className="text-left p-3 font-semibold">Created</th>
                          <th className="text-left p-3 font-semibold">Size</th>
                          <th className="text-right p-3 font-semibold">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {backups.map((backup, index) => (
                          <tr key={backup.backup_id} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                            <td className="p-3 font-mono text-sm">{backup.filename}</td>
                            <td className="p-3">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                backup.backup_type === 'scheduled' 
                                  ? 'bg-purple-100 text-purple-700' 
                                  : 'bg-blue-100 text-blue-700'
                              }`}>
                                {backup.backup_type === 'scheduled' ? 'Auto' : 'Manual'}
                              </span>
                            </td>
                            <td className="p-3 text-sm text-slate-600">
                              {new Date(backup.created_at).toLocaleString()}
                            </td>
                            <td className="p-3 text-sm text-slate-600">
                              {formatFileSize(backup.file_size)}
                            </td>
                            <td className="p-3 text-right">
                              <div className="flex justify-end gap-2">
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => handleDownloadBackup(backup.backup_id, backup.filename)}
                                  title="Download backup"
                                >
                                  <Download className="w-4 h-4" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="destructive"
                                  onClick={() => handleDeleteBackup(backup.backup_id)}
                                  title="Delete backup"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
              )}
            </Card>
          </div>
        )}
        
        {/* Market Movers Stock List Panel */}
        {activeTab === 'market' && (
          <Card className="w-full">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <RefreshCw className="w-5 h-5" /> Market Movers Stock List
              </CardTitle>
              <CardDescription>
                Manage the list of stocks used for the Market Movers panel. 
                The more stocks you add, the more likely you&apos;ll see 15 gainers and 15 losers.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Status */}
              <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-700">
                    Current Source: <span className={marketMoversList.source === 'custom' ? 'text-emerald-600' : 'text-blue-600'}>
                      {marketMoversList.source === 'custom' ? 'Custom List' : 'Default List'}
                    </span>
                  </p>
                  <p className="text-sm text-slate-500">
                    {marketMoversList.count} symbols loaded
                    {marketMoversList.lastUpdated && ` • Updated: ${new Date(marketMoversList.lastUpdated).toLocaleString()}`}
                  </p>
                </div>
                {isLoadingMarketMovers && <Loader2 className="w-5 h-5 animate-spin text-slate-400" />}
              </div>
              
              {/* Upload/Download buttons */}
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  onClick={handleDownloadMarketMoversList}
                  disabled={isLoadingMarketMovers}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download List
                </Button>
                <label className="inline-flex items-center px-4 py-2 border border-slate-300 rounded-md text-sm font-medium text-slate-700 bg-white hover:bg-slate-50 cursor-pointer">
                  <Upload className="w-4 h-4 mr-2" />
                  Upload List
                  <input
                    type="file"
                    accept=".txt,.csv"
                    onChange={handleUploadMarketMoversList}
                    className="hidden"
                  />
                </label>
                <Button
                  variant="outline"
                  onClick={handleResetMarketMoversList}
                  disabled={isSavingMarketMovers || marketMoversList.source === 'default'}
                  className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Reset to Default
                </Button>
              </div>
              
              {/* Textarea for editing */}
              <div>
                <Label className="mb-2 block">
                  Stock Symbols (one per line, or comma/space separated)
                </Label>
                <textarea
                  value={marketMoversText}
                  onChange={(e) => setMarketMoversText(e.target.value)}
                  placeholder="AAPL&#10;MSFT&#10;GOOGL&#10;NVDA&#10;..."
                  className="w-full h-64 px-3 py-2 border border-slate-300 rounded-md text-sm font-mono resize-y"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Currently: {marketMoversText.split(/[\n,\s]+/).filter(s => s.trim()).length} symbols in editor
                </p>
              </div>
              
              {/* Save button */}
              <div className="flex gap-2">
                <Button 
                  onClick={handleSaveMarketMoversList} 
                  disabled={isSavingMarketMovers}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {isSavingMarketMovers ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                  ) : (
                    <><Save className="w-4 h-4 mr-2" /> Save Stock List</>
                  )}
                </Button>
                <Button 
                  variant="outline"
                  onClick={fetchMarketMoversList}
                  disabled={isLoadingMarketMovers}
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${isLoadingMarketMovers ? 'animate-spin' : ''}`} />
                  Reload
                </Button>
              </div>
              
              {/* Tips */}
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                <p className="font-medium text-blue-800 mb-2">💡 Tips for better Market Movers:</p>
                <ul className="list-disc list-inside text-blue-700 space-y-1">
                  <li>Include volatile stocks (meme stocks, small caps) for bigger daily moves</li>
                  <li>Add sector ETFs (XLK, XLF, XLE) for sector representation</li>
                  <li>Include popular crypto proxies (COIN, MARA, RIOT)</li>
                  <li>More stocks = better chance of finding 15 gainers/losers</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Header Settings Panel */}
        {activeTab === 'header' && (
          <Card className="w-full">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Layout className="w-5 h-5" /> Header Settings
              </CardTitle>
              <CardDescription>Configure the application header</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Site Title</Label>
                <Input
                  value={settings.header?.title || ''}
                  onChange={(e) => setSettings({ ...settings, header: { ...settings.header, title: e.target.value } })}
                  placeholder="Portfolio Tracker"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="showLogo"
                  checked={settings.header?.showLogo || false}
                  onChange={(e) => setSettings({ ...settings, header: { ...settings.header, showLogo: e.target.checked } })}
                  className="w-4 h-4"
                />
                <Label htmlFor="showLogo">Show Logo</Label>
              </div>
              <Button onClick={() => handleSaveSettings('header')} disabled={isLoading}>
                {isLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Header Settings
              </Button>
            </CardContent>
          </Card>
        )}
        
        {/* Footer Settings Panel */}
        {activeTab === 'footer' && (
          <Card className="w-full">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Layout className="w-5 h-5" /> Footer Settings
              </CardTitle>
              <CardDescription>Configure the application footer text shown on all pages</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Footer Text</Label>
                <Input
                  value={settings.footer?.text || ''}
                  onChange={(e) => setSettings({ ...settings, footer: { text: e.target.value } })}
                  placeholder="Your company tagline or copyright notice"
                />
                <p className="text-sm text-slate-500 mt-1">This text will appear at the bottom of all pages</p>
              </div>
              <Button onClick={() => handleSaveSettings('footer')} disabled={isLoading}>
                {isLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Footer Settings
              </Button>
            </CardContent>
          </Card>
        )}
        
        {/* Communications Panel */}
        {activeTab === 'communications' && (
          <Card className="w-full">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5" /> Communications
              </CardTitle>
              <CardDescription>Set up automated emails and notifications</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Daily Summary Section */}
              <div className="mb-8 border-b pb-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-emerald-100 rounded-lg">
                      <Mail className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">Daily Portfolio Summary</h3>
                      <p className="text-sm text-slate-500">Automated daily email with portfolio performance</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={dailySummarySettings.enabled}
                      onCheckedChange={(checked) => setDailySummarySettings({...dailySummarySettings, enabled: checked})}
                    />
                    <span className={`text-sm font-medium ${dailySummarySettings.enabled ? 'text-emerald-600' : 'text-slate-400'}`}>
                      {dailySummarySettings.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                </div>
                
                <div className="bg-slate-50 rounded-lg p-4 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Send Time (Daily)</Label>
                      <Input
                        type="time"
                        value={dailySummarySettings.time || '08:00'}
                        onChange={(e) => setDailySummarySettings({...dailySummarySettings, time: e.target.value})}
                        className="mt-1"
                      />
                      <p className="text-xs text-slate-500 mt-1">Email will be sent at this time every day</p>
                    </div>
                    
                    <div>
                      <Label>Recipients</Label>
                      <p className="text-sm text-slate-600 mt-1">
                        All users with email notifications enabled will receive this email.
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        Manage user email preferences in the Users panel.
                      </p>
                    </div>
                  </div>
                  
                  {dailySummarySettings.lastRun && (
                    <div className="flex items-center gap-2 text-sm text-slate-600 bg-white p-2 rounded">
                      <Check className="w-4 h-4 text-green-500" />
                      Last sent: {new Date(dailySummarySettings.lastRun).toLocaleString()}
                      {dailySummarySettings.lastSentCount > 0 && (
                        <span>({dailySummarySettings.lastSentCount} emails)</span>
                      )}
                    </div>
                  )}
                  
                  <div className="flex flex-wrap gap-2 pt-2">
                    <Button 
                      onClick={handleSaveDailySummarySettings}
                      disabled={isLoading}
                    >
                      {isLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                      Save Settings
                    </Button>
                    
                    <Button 
                      variant="outline"
                      onClick={handlePreviewDailySummary}
                      disabled={isLoadingPreview}
                    >
                      {isLoadingPreview ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
                      Preview Email
                    </Button>
                    
                    <Button 
                      variant="outline"
                      onClick={handleSendDailySummaryNow}
                      disabled={isSendingDailySummary}
                    >
                      {isSendingDailySummary ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                      Send Now
                    </Button>
                  </div>
                </div>
              </div>
              
              {/* Preview Modal */}
              {dailySummaryPreview && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                    <div className="flex justify-between items-center p-4 border-b">
                      <h2 className="text-lg font-semibold">Daily Summary Email Preview</h2>
                      <Button variant="ghost" size="sm" onClick={() => setDailySummaryPreview(null)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="flex-1 overflow-y-auto p-4 bg-slate-100">
                      <div 
                        className="mx-auto"
                        style={{ maxWidth: '600px' }}
                        dangerouslySetInnerHTML={{ __html: dailySummaryPreview }} 
                      />
                    </div>
                    <div className="p-4 border-t flex justify-end gap-2">
                      <Button variant="outline" onClick={() => setDailySummaryPreview(null)}>
                        Close
                      </Button>
                      <Button onClick={handleSendDailySummaryNow} disabled={isSendingDailySummary}>
                        {isSendingDailySummary ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                        Send to All Users
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Custom Communications Section */}
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-lg">Custom Communications</h3>
                <Button onClick={() => setShowAddCommunication(true)}>
                  <Plus className="w-4 h-4 mr-2" /> New Communication
                </Button>
              </div>
              
              {/* Communication List */}
              {communications.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-medium">No communications set up</p>
                  <p className="text-sm">Create your first automated email or notification</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {communications.map((comm) => (
                    <div 
                      key={comm.id} 
                      className={`border rounded-lg p-4 ${comm.enabled ? 'bg-white' : 'bg-slate-50 opacity-75'}`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-lg">
                              {communicationTypes.find(t => t.value === comm.type)?.icon || '✉️'}
                            </span>
                            <h3 className="font-semibold text-lg">{comm.name}</h3>
                            {comm.enabled ? (
                              <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">Active</span>
                            ) : (
                              <span className="px-2 py-0.5 bg-slate-100 text-slate-500 text-xs rounded-full">Paused</span>
                            )}
                          </div>
                          <p className="text-sm text-slate-600 mb-2">
                            <strong>Subject:</strong> {comm.subject}
                          </p>
                          <div className="flex flex-wrap gap-4 text-xs text-slate-500">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {comm.schedule?.frequency === 'daily' && `Daily at ${comm.schedule?.time || '08:00'}`}
                              {comm.schedule?.frequency === 'weekly' && `Weekly at ${comm.schedule?.time || '08:00'}`}
                              {comm.schedule?.frequency === 'monthly' && `Monthly at ${comm.schedule?.time || '08:00'}`}
                            </span>
                            <span className="flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              {comm.recipients === 'all' && 'All users'}
                              {comm.recipients === 'email_enabled' && 'Email-enabled users'}
                              {comm.recipients === 'specific' && `${comm.specific_emails?.length || 0} specific`}
                            </span>
                            {comm.lastSent && (
                              <span className="flex items-center gap-1">
                                <Check className="w-3 h-3 text-green-500" />
                                Last sent: {new Date(comm.lastSent).toLocaleDateString()}
                                {comm.lastSentCount && ` (${comm.lastSentCount} emails)`}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSendNow(comm.id)}
                            disabled={isSendingCommunication === comm.id}
                            title="Send Now"
                          >
                            {isSendingCommunication === comm.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Send className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleToggleCommunication(comm.id)}
                            title={comm.enabled ? 'Pause' : 'Enable'}
                          >
                            {comm.enabled ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setEditingCommunication(comm)}
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteCommunication(comm.id)}
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Add/Edit Communication Modal */}
              {(showAddCommunication || editingCommunication) && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                  <div className="bg-white rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                    <h2 className="text-xl font-bold mb-4">
                      {editingCommunication ? 'Edit Communication' : 'New Communication'}
                    </h2>
                    
                    <div className="space-y-4">
                      {/* Name */}
                      <div>
                        <Label>Name</Label>
                        <Input
                          value={editingCommunication?.name || newCommunication.name}
                          onChange={(e) => {
                            if (editingCommunication) {
                              setEditingCommunication({...editingCommunication, name: e.target.value});
                            } else {
                              setNewCommunication({...newCommunication, name: e.target.value});
                            }
                          }}
                          placeholder="e.g., Daily Portfolio Summary"
                        />
                      </div>
                      
                      {/* Type */}
                      <div>
                        <Label>Type</Label>
                        <select
                          className="w-full p-2 border rounded-md"
                          value={editingCommunication?.type || newCommunication.type}
                          onChange={(e) => {
                            if (editingCommunication) {
                              setEditingCommunication({...editingCommunication, type: e.target.value});
                            } else {
                              setNewCommunication({...newCommunication, type: e.target.value});
                            }
                          }}
                        >
                          {communicationTypes.map(type => (
                            <option key={type.value} value={type.value}>
                              {type.icon} {type.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      
                      {/* Subject */}
                      <div>
                        <Label>Email Subject</Label>
                        <Input
                          value={editingCommunication?.subject || newCommunication.subject}
                          onChange={(e) => {
                            if (editingCommunication) {
                              setEditingCommunication({...editingCommunication, subject: e.target.value});
                            } else {
                              setNewCommunication({...newCommunication, subject: e.target.value});
                            }
                          }}
                          placeholder="e.g., Your Daily Portfolio Update"
                        />
                      </div>
                      
                      {/* Template */}
                      <div>
                        <Label>Email Template (HTML)</Label>
                        <p className="text-xs text-slate-500 mb-2">
                          Available placeholders: {"{{user_name}}"}, {"{{user_email}}"}, {"{{date}}"}
                        </p>
                        <textarea
                          className="w-full p-3 border rounded-md font-mono text-sm min-h-[200px]"
                          value={editingCommunication?.template || newCommunication.template}
                          onChange={(e) => {
                            if (editingCommunication) {
                              setEditingCommunication({...editingCommunication, template: e.target.value});
                            } else {
                              setNewCommunication({...newCommunication, template: e.target.value});
                            }
                          }}
                          placeholder={`<h1>Hello {{user_name}}!</h1>
<p>Here's your portfolio update for {{date}}.</p>
<p>Best regards,<br>PortfolioASaurus</p>`}
                        />
                      </div>
                      
                      {/* Schedule */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Frequency</Label>
                          <select
                            className="w-full p-2 border rounded-md"
                            value={(editingCommunication?.schedule || newCommunication.schedule)?.frequency || 'daily'}
                            onChange={(e) => {
                              const schedule = {...(editingCommunication?.schedule || newCommunication.schedule), frequency: e.target.value};
                              if (editingCommunication) {
                                setEditingCommunication({...editingCommunication, schedule});
                              } else {
                                setNewCommunication({...newCommunication, schedule});
                              }
                            }}
                          >
                            {frequencyOptions.map(opt => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Label>Time</Label>
                          <Input
                            type="time"
                            value={(editingCommunication?.schedule || newCommunication.schedule)?.time || '08:00'}
                            onChange={(e) => {
                              const schedule = {...(editingCommunication?.schedule || newCommunication.schedule), time: e.target.value};
                              if (editingCommunication) {
                                setEditingCommunication({...editingCommunication, schedule});
                              } else {
                                setNewCommunication({...newCommunication, schedule});
                              }
                            }}
                          />
                        </div>
                      </div>
                      
                      {/* Days (for weekly) */}
                      {((editingCommunication?.schedule || newCommunication.schedule)?.frequency === 'daily' || 
                        (editingCommunication?.schedule || newCommunication.schedule)?.frequency === 'weekly') && (
                        <div>
                          <Label>Days</Label>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {dayOptions.map(day => {
                              const schedule = editingCommunication?.schedule || newCommunication.schedule;
                              const isSelected = schedule?.days?.includes(day.value);
                              return (
                                <button
                                  key={day.value}
                                  type="button"
                                  className={`px-3 py-1 rounded-full text-sm border ${
                                    isSelected 
                                      ? 'bg-blue-500 text-white border-blue-500' 
                                      : 'bg-white text-slate-600 border-slate-300 hover:border-blue-400'
                                  }`}
                                  onClick={() => {
                                    const currentDays = schedule?.days || [];
                                    const newDays = isSelected
                                      ? currentDays.filter(d => d !== day.value)
                                      : [...currentDays, day.value];
                                    const newSchedule = {...schedule, days: newDays};
                                    if (editingCommunication) {
                                      setEditingCommunication({...editingCommunication, schedule: newSchedule});
                                    } else {
                                      setNewCommunication({...newCommunication, schedule: newSchedule});
                                    }
                                  }}
                                >
                                  {day.label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      
                      {/* Recipients */}
                      <div>
                        <Label>Recipients</Label>
                        <select
                          className="w-full p-2 border rounded-md"
                          value={editingCommunication?.recipients || newCommunication.recipients}
                          onChange={(e) => {
                            if (editingCommunication) {
                              setEditingCommunication({...editingCommunication, recipients: e.target.value});
                            } else {
                              setNewCommunication({...newCommunication, recipients: e.target.value});
                            }
                          }}
                        >
                          <option value="all">All Users</option>
                          <option value="email_enabled">Users with Email Enabled</option>
                          <option value="specific">Specific Emails</option>
                        </select>
                      </div>
                      
                      {/* Specific Emails */}
                      {(editingCommunication?.recipients || newCommunication.recipients) === 'specific' && (
                        <div>
                          <Label>Email Addresses (one per line)</Label>
                          <textarea
                            className="w-full p-2 border rounded-md"
                            rows={3}
                            value={(editingCommunication?.specific_emails || newCommunication.specific_emails)?.join('\n') || ''}
                            onChange={(e) => {
                              const emails = e.target.value.split('\n').map(e => e.trim()).filter(e => e);
                              if (editingCommunication) {
                                setEditingCommunication({...editingCommunication, specific_emails: emails});
                              } else {
                                setNewCommunication({...newCommunication, specific_emails: emails});
                              }
                            }}
                            placeholder="user1@example.com&#10;user2@example.com"
                          />
                        </div>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                      <Button 
                        variant="outline" 
                        onClick={() => {
                          setShowAddCommunication(false);
                          setEditingCommunication(null);
                        }}
                      >
                        Cancel
                      </Button>
                      <Button 
                        onClick={editingCommunication ? handleUpdateCommunication : handleCreateCommunication}
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Save className="w-4 h-4 mr-2" />
                        )}
                        {editingCommunication ? 'Save Changes' : 'Create Communication'}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Email Activity Log Section */}
              <div className="mt-8 pt-8 border-t">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h3 className="font-semibold text-lg flex items-center gap-2">
                      <Clock className="w-5 h-5" /> Email Activity Log
                    </h3>
                    <p className="text-sm text-slate-500">Track all sent emails and their status</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <select
                      className="text-sm border rounded-md px-2 py-1"
                      value={emailActivityFilter}
                      onChange={(e) => {
                        setEmailActivityFilter(e.target.value);
                        fetchEmailActivity(e.target.value);
                      }}
                    >
                      <option value="all">All Types</option>
                      <option value="daily_summary">Daily Summary</option>
                      <option value="communication">Communications</option>
                      <option value="test">Test Emails</option>
                    </select>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchEmailActivity(emailActivityFilter)}
                      disabled={isLoadingActivity}
                    >
                      {isLoadingActivity ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (window.confirm('Clear all activity logs older than 30 days?')) {
                          handleClearActivityLogs(30);
                        }
                      }}
                      className="text-slate-500"
                    >
                      <Trash2 className="w-4 h-4 mr-1" /> Clear Old
                    </Button>
                  </div>
                </div>
                
                {/* Activity Log Table */}
                {isLoadingActivity ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                  </div>
                ) : emailActivityLogs.length === 0 ? (
                  <div className="text-center py-12 text-slate-500 bg-slate-50 rounded-lg">
                    <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium">No email activity yet</p>
                    <p className="text-sm">Sent emails will appear here</p>
                  </div>
                ) : (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="max-h-96 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-slate-50 sticky top-0">
                          <tr>
                            <th className="text-left px-4 py-2 font-medium text-slate-600">Time</th>
                            <th className="text-left px-4 py-2 font-medium text-slate-600">Type</th>
                            <th className="text-left px-4 py-2 font-medium text-slate-600">Recipient</th>
                            <th className="text-left px-4 py-2 font-medium text-slate-600">Subject</th>
                            <th className="text-left px-4 py-2 font-medium text-slate-600">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {emailActivityLogs.map((log, index) => (
                            <tr key={log.id || index} className="hover:bg-slate-50">
                              <td className="px-4 py-2 whitespace-nowrap text-slate-500">
                                {new Date(log.sentAt).toLocaleString()}
                              </td>
                              <td className="px-4 py-2">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                  log.type === 'daily_summary' ? 'bg-blue-100 text-blue-700' :
                                  log.type === 'communication' ? 'bg-purple-100 text-purple-700' :
                                  log.type === 'test' ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-slate-100 text-slate-700'
                                }`}>
                                  {log.type === 'daily_summary' ? '🦕 Daily' :
                                   log.type === 'communication' ? '📧 Comm' :
                                   log.type === 'test' ? '🧪 Test' : log.type}
                                </span>
                              </td>
                              <td className="px-4 py-2 text-slate-700 max-w-xs truncate">
                                {log.recipient}
                              </td>
                              <td className="px-4 py-2 text-slate-600 max-w-xs truncate" title={log.subject}>
                                {log.subject}
                              </td>
                              <td className="px-4 py-2">
                                {log.status === 'sent' ? (
                                  <span className="inline-flex items-center gap-1 text-green-600">
                                    <Check className="w-3 h-3" /> Sent
                                  </span>
                                ) : log.status === 'sending' ? (
                                  <span className="inline-flex items-center gap-1 text-amber-600">
                                    <Loader2 className="w-3 h-3 animate-spin" /> Sending
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 text-red-600" title={log.error || log.errorMessage || ''}>
                                    <X className="w-3 h-3" /> Failed
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="bg-slate-50 px-4 py-2 text-sm text-slate-500 border-t">
                      Showing {emailActivityLogs.length} of {emailActivityTotal} entries
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* SMTP Settings Panel */}
        {activeTab === 'smtp' && (
          <Card className="w-full">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5" /> SMTP Settings
              </CardTitle>
              <CardDescription>Configure email server settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>SMTP Host</Label>
                  <Input
                    value={settings.smtp?.host || ''}
                    onChange={(e) => setSettings({ ...settings, smtp: { ...settings.smtp, host: e.target.value } })}
                    placeholder="smtp.example.com"
                  />
                </div>
                <div>
                  <Label>SMTP Port</Label>
                  <Input
                    type="number"
                    value={settings.smtp?.port || 587}
                    onChange={(e) => setSettings({ ...settings, smtp: { ...settings.smtp, port: parseInt(e.target.value) || 587 } })}
                    placeholder="587"
                  />
                </div>
                <div>
                  <Label>Username</Label>
                  <Input
                    value={settings.smtp?.username || ''}
                    onChange={(e) => setSettings({ ...settings, smtp: { ...settings.smtp, username: e.target.value } })}
                    placeholder="username"
                  />
                </div>
                <div>
                  <Label>Password</Label>
                  <div className="relative">
                    <Input
                      type={showSmtpPassword ? "text" : "password"}
                      value={settings.smtp?.password || ''}
                      onChange={(e) => setSettings({ ...settings, smtp: { ...settings.smtp, password: e.target.value } })}
                      placeholder="••••••••"
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowSmtpPassword(!showSmtpPassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600 rounded"
                      data-testid="smtp-password-toggle"
                    >
                      {showSmtpPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label>From Email</Label>
                  <Input
                    type="email"
                    value={settings.smtp?.fromEmail || ''}
                    onChange={(e) => setSettings({ ...settings, smtp: { ...settings.smtp, fromEmail: e.target.value } })}
                    placeholder="noreply@example.com"
                  />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <input
                    type="checkbox"
                    id="smtpEnabled"
                    checked={settings.smtp?.enabled || false}
                    onChange={(e) => setSettings({ ...settings, smtp: { ...settings.smtp, enabled: e.target.checked } })}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="smtpEnabled">Enable SMTP</Label>
                </div>
              </div>
              <Button onClick={() => handleSaveSettings('smtp')} disabled={isLoading}>
                {isLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save SMTP Settings
              </Button>
              
              {/* Test Email Section */}
              <div className="mt-6 pt-6 border-t border-slate-200">
                <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <Send className="w-4 h-4" />
                  Send Test Email
                </h3>
                <div className="flex gap-3">
                  <Input
                    type="email"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    placeholder="Enter email address to test"
                    className="flex-1"
                    disabled={isSendingTest}
                  />
                  <Button 
                    onClick={handleSendTestEmail} 
                    disabled={isSendingTest || !testEmail || !settings.smtp?.enabled}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {isSendingTest ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
                    ) : (
                      <><Send className="w-4 h-4 mr-2" /> Send Test</>
                    )}
                  </Button>
                </div>
                {!settings.smtp?.enabled && (
                  <p className="text-sm text-amber-600 mt-2">
                    ⚠️ Enable SMTP and save settings before sending a test email
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      <Footer />
      
      {/* Delete User Confirmation Modal (for users with stocks) */}
      {deleteConfirmation.show && deleteConfirmation.user && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md mx-4 w-full">
            {/* Warning Header */}
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">Delete User?</h3>
                <p className="text-sm text-slate-500">This action cannot be undone</p>
              </div>
            </div>
            
            {/* User Info */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-red-800 font-medium mb-2">
                ⚠️ This user has an active portfolio:
              </p>
              <div className="space-y-1 text-sm">
                <p className="text-slate-700">
                  <span className="font-medium">Email:</span> {deleteConfirmation.user.email}
                </p>
                <p className="text-slate-700">
                  <span className="font-medium">Stocks:</span> {deleteConfirmation.user.stockCount} position{deleteConfirmation.user.stockCount !== 1 ? 's' : ''}
                </p>
                <p className="text-slate-700">
                  <span className="font-medium">Portfolio Value:</span>{' '}
                  <span className="text-red-600 font-bold">
                    ${(deleteConfirmation.user.portfolioValue || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                  </span>
                </p>
              </div>
            </div>
            
            {/* Confirmation Input */}
            <div className="mb-4">
              <Label className="text-sm text-slate-700">
                Type <span className="font-mono bg-slate-100 px-1 rounded">{deleteConfirmation.user.email}</span> to confirm:
              </Label>
              <Input
                type="text"
                value={deleteConfirmation.confirmEmail}
                onChange={(e) => setDeleteConfirmation(prev => ({ ...prev, confirmEmail: e.target.value }))}
                placeholder="Enter email to confirm"
                className="mt-2"
                autoComplete="off"
              />
            </div>
            
            {/* Buttons */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setDeleteConfirmation({ show: false, user: null, confirmEmail: '', isDeleting: false })}
                disabled={deleteConfirmation.isDeleting}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                className="flex-1"
                onClick={handleConfirmDelete}
                disabled={deleteConfirmation.confirmEmail !== deleteConfirmation.user.email || deleteConfirmation.isDeleting}
              >
                {deleteConfirmation.isDeleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete User & Data
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Password Reset Modal */}
      {passwordReset.show && passwordReset.user && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md mx-4 w-full">
            {/* Header */}
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                <KeyRound className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">Reset Password</h3>
                <p className="text-sm text-slate-500">{passwordReset.user.email}</p>
              </div>
            </div>
            
            {/* Password Input */}
            <div className="mb-4">
              <Label className="text-sm text-slate-700">New Password</Label>
              <Input
                type="text"
                value={passwordReset.newPassword}
                onChange={(e) => setPasswordReset(prev => ({ ...prev, newPassword: e.target.value }))}
                placeholder="Enter new password (min 4 characters)"
                className="mt-2"
                autoComplete="off"
              />
              <p className="text-xs text-slate-500 mt-1">The user will need to use this password on their next login.</p>
            </div>
            
            {/* Buttons */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setPasswordReset({ show: false, user: null, newPassword: '', isResetting: false })}
                disabled={passwordReset.isResetting}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-purple-600 hover:bg-purple-700"
                onClick={handleResetPassword}
                disabled={!passwordReset.newPassword || passwordReset.newPassword.length < 4 || passwordReset.isResetting}
              >
                {passwordReset.isResetting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Resetting...
                  </>
                ) : (
                  <>
                    <KeyRound className="w-4 h-4 mr-2" />
                    Reset Password
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
